import React, { useEffect, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';

import { useQueryClient } from '@tanstack/react-query';

import { requestApi } from 'api/Api';
import { createPracticeOwner } from 'api/Practice';

import { iaRa } from 'lib/helpers/utility';

import Input from 'components/shared/Input/Input';
import Modal from 'components/shared/Modal/Modal';
import { showAlert } from 'components/shared/Alert/Alert';
import SegmentedControl from 'components/shared/SegmentedControl/SegmentedControl';

import { TABS } from './lib/helpers';
import InviteForm from './components/InviteForm';
import AllUsersList from './components/AllUsersList';
import PracticeUsers from './components/PracticeUsers';
import useInviteOwnerFormik from './lib/useInviteOwnerFormik';

const CreateOrganization = ({ isOpen, handleClose }) => {
  const { practice } = useOutletContext();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [orgName, setOrgName] = useState('');
  const [tabIndex, setTabIndex] = useState(TABS.PRACTICE);
  const [selectedUser, setSelectedUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(null);

  const formik = useInviteOwnerFormik();

  useEffect(() => {
    if (!isOpen) {
      setOrgName('');
      setTabIndex(TABS.PRACTICE);
      setSelectedUser(null);
      formik.resetForm();
    }
  }, [isOpen]);

  useEffect(() => setSelectedUser(null), [tabIndex]);

  const done = (msg) => {
    showAlert({ color: 'success', message: msg });
    queryClient.invalidateQueries(['practice']);
    handleClose();
  };

  const handleCreate = async () => {
    if (!orgName.trim())
      return showAlert({ color: 'danger', message: 'Please enter an organization name.' });
    if (!selectedUser)
      return showAlert({
        color: 'danger',
        message: 'Please select a user as the organization owner.'
      });
    setLoading(true);
    try {
      const { code } = await createPracticeOwner(navigate, {
        practice_id: practice.id,
        user_id: selectedUser.id,
        name: orgName.trim()
      });
      if (code === 0) {
        done('Organization created successfully!');
      } else {
        showAlert({ color: 'danger', message: 'Something went wrong. Please try again.' });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInvite = async () => {
    if (!orgName.trim())
      return showAlert({ color: 'danger', message: 'Please enter an organization name.' });
    const errors = await formik.validateForm();
    formik.setTouched({ f_name: true, l_name: true, email: true });
    if (Object.keys(errors).length > 0) return;
    setLoading(true);
    try {
      const { f_name, l_name, gender, dob, email, phone } = formik.values;
      const res = await requestApi({
        url: '/api/practice_owner/invite',
        navigate,
        params: {
          user: { f_name, l_name, gender, dob, email, phone_number: phone },
          practice_id: practice.id,
          name: orgName.trim()
        }
      });
      if (res.code === 0) {
        done('Invitation sent and organization created successfully!');
      } else {
        showAlert({ color: 'danger', message: 'Something went wrong. Please try again.' });
      }
    } finally {
      setLoading(false);
    }
  };

  const isInvite = tabIndex === TABS.INVITE;
  const canCreate = !isInvite && orgName.trim() && selectedUser;
  const segments = [
    { label: 'Practice users', value: TABS.PRACTICE, count: iaRa(practice?.members).length },
    { label: 'All users', value: TABS.ALL, ...(totalCount != null && { count: totalCount }) },
    { label: 'Invite new owner by email', value: TABS.INVITE }
  ];

  return (
    <Modal
      handleOpen={isOpen}
      handleClose={handleClose}
      title="New organization"
      bodyClassName="!p-0"
      customStyling={{ width: '640px' }}
      footerActions={{
        primary: {
          text: isInvite ? 'Send invitation & create organization' : 'Create organization',
          onClick: isInvite ? handleInvite : handleCreate,
          isDisabled: isInvite ? !orgName.trim() : !canCreate,
          isLoading: loading
        }
      }}>
      <div className="flex flex-col">
        <div className="px-4 pb-2 pt-4">
          <Input
            label="Organization name"
            placeholder="Enter organization name"
            required
            value={orgName}
            onChange={(e) => setOrgName(e.target.value)}
          />
        </div>
        <div className="px-4 pb-1 pt-2">
          <label className="mb-2 block text-sm font-500 text-neutral-800">
            Set organization owner <span className="text-danger-500">*</span>
          </label>
        </div>
        <div className="px-4">
          <SegmentedControl
            items={segments}
            active={tabIndex}
            onClick={(i) => setTabIndex(i.value)}
          />
        </div>
        {tabIndex === TABS.PRACTICE && (
          <PracticeUsers
            members={practice?.members}
            isOpen={isOpen}
            selectedUser={selectedUser}
            setSelectedUser={setSelectedUser}
          />
        )}
        {tabIndex === TABS.ALL && (
          <AllUsersList
            isOpen={isOpen}
            selectedUser={selectedUser}
            setSelectedUser={setSelectedUser}
            onTotalCount={setTotalCount}
          />
        )}
        {tabIndex === TABS.INVITE && <InviteForm formik={formik} />}
      </div>
    </Modal>
  );
};

export default CreateOrganization;
